/**
 * Pack Progress Page (`/account/packs/:packId`)
 *
 * Authenticated. Shows the seller's view of their own pack — completion
 * percentage, status, and the "Share with solicitor" controls
 * (Feature 37). This is the home of the share-link generation flow per
 * Requirement 26.1.
 *
 * The page deliberately does NOT include field editing or document
 * uploads — those live on the existing /selling-documents flow. This
 * page is a status hub plus the share-link affordance.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { SharePackControls } from '../components/SharePackControls';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface PackSummary {
  packId: string;
  address?: string;
  propertyType?: string;
  status: 'active' | 'processing' | 'complete';
  completionPercentage: number;
  isPaid: boolean;
  createdAt: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; pack: PackSummary }
  | { kind: 'not-found' }
  | { kind: 'error'; message: string };

export default function PackProgressPage() {
  const { packId } = useParams<{ packId: string }>();
  const navigate = useNavigate();
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !packId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/packs`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (cancelled) return;
        if (response.status === 401) {
          navigate('/login', { replace: true });
          return;
        }
        if (!response.ok) {
          setState({ kind: 'error', message: 'Could not load this pack.' });
          return;
        }
        const data = (await response.json()) as { packs: PackSummary[] };
        const found = data.packs.find((p) => p.packId === packId);
        if (!found) {
          setState({ kind: 'not-found' });
          return;
        }
        setState({ kind: 'ready', pack: found });
      } catch {
        if (!cancelled) {
          setState({ kind: 'error', message: 'Could not load this pack.' });
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [packId, isAuthenticated, navigate]);

  if (authLoading || (!isAuthenticated && state.kind === 'loading')) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Pack progress — UKPropertyChecks</title>
        <meta name="robots" content="noindex,nofollow" />
        <link
          rel="canonical"
          href={`https://ukpropertychecks.co.uk/account/packs/${packId ?? ''}`}
        />
      </Helmet>
      <main id="main-content" className="min-h-screen bg-slate-50">
        <div className="container-main section-spacing">
          <nav aria-label="Breadcrumb" className="text-sm text-slate-700 mb-6">
            <Link to="/account" className="text-blue-700">
              Your account
            </Link>
            <span className="mx-2">/</span>
            <span className="text-slate-700">Pack progress</span>
          </nav>

          {state.kind === 'loading' && <p className="text-slate-700">Loading…</p>}

          {state.kind === 'not-found' && (
            <section>
              <h1 className="text-2xl font-semibold text-slate-950">Pack not found</h1>
              <p className="mt-3 text-slate-800">
                We could not find this pack. It may have been deleted.
              </p>
              <p className="mt-6">
                <Link to="/account" className="text-blue-700">
                  Back to your account
                </Link>
              </p>
            </section>
          )}

          {state.kind === 'error' && (
            <section>
              <h1 className="text-2xl font-semibold text-slate-950">
                Could not load pack
              </h1>
              <p className="error mt-3" role="alert">
                {state.message}
              </p>
            </section>
          )}

          {state.kind === 'ready' && (
            <ReadyView pack={state.pack} packId={packId!} />
          )}
        </div>
      </main>
    </>
  );
}

function ReadyView({ pack, packId }: { pack: PackSummary; packId: string }) {
  const canShare = pack.completionPercentage > 0;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-950">
          {pack.address || 'Your seller’s pack'}
        </h1>
        <p className="mt-2 text-slate-700">
          Created {new Date(pack.createdAt).toLocaleDateString('en-GB')}
        </p>
      </header>

      <section
        className="rounded-lg border border-slate-200 bg-white p-6"
        aria-labelledby="overall-progress-heading"
      >
        <h2 id="overall-progress-heading" className="text-lg font-semibold text-slate-950">
          Overall progress
        </h2>
        <div className="mt-4">
          <div
            role="progressbar"
            aria-valuenow={pack.completionPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Pack completion percentage"
            className="h-3 w-full rounded-full bg-slate-100 overflow-hidden"
          >
            <div
              className="h-full bg-emerald-700"
              style={{ width: `${pack.completionPercentage}%` }}
            />
          </div>
          <p className="mt-2 text-2xl font-semibold text-slate-950">
            {pack.completionPercentage}%
          </p>
        </div>
        <div className="mt-6">
          <Link
            to={`/selling-documents?packId=${encodeURIComponent(packId)}`}
            className="btn-secondary no-underline"
          >
            Continue editing
          </Link>
        </div>
      </section>

      <SharePackControls packId={packId} canShare={canShare} />
    </div>
  );
}
